import { useState, useEffect, forwardRef, useRef } from 'react';
import { Check, Send, Sparkles, Star } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { submitPublicForm } from '../../../services/formService';
import { useFormTracking } from '../../../hooks/useFormTracking';
import type { FormWithFields, FormField, RatingFieldOptions } from '../../../types/forms';
import type { PageType } from '../../../types/analytics';
import { logger } from '../../../lib/logger';

interface FormBlockProps {
  settings: { title: string; formId: string | null };
  primaryColor: string;
  originPageType?: PageType;
  originSlug?: string;
}

export const FormBlock = forwardRef<HTMLElement, FormBlockProps>(({ settings, primaryColor, originPageType, originSlug }, ref) => {
  const [form, setForm] = useState<FormWithFields | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const hasStartedRef = useRef(false);

  const { trackStart } = useFormTracking({
    formId: settings.formId || '',
    originPageType: originPageType || null,
    originSlug: originSlug || null,
    enabled: !!settings.formId && !!form,
  });

  useEffect(() => {
    if (settings.formId) {
      loadForm();
    } else {
      setIsLoading(false);
    }
  }, [settings.formId]);

  const loadForm = async () => {
    if (!settings.formId) return;
    try {
      const { data: formData } = await supabase
        .from('forms')
        .select('*')
        .eq('id', settings.formId)
        .eq('is_published', true)
        .maybeSingle();

      if (formData) {
        const { data: fields } = await supabase
          .from('form_fields')
          .select('*')
          .eq('form_id', formData.id)
          .order('field_order', { ascending: true });

        const formWithFields: FormWithFields = { ...formData, fields: fields || [] };
        setForm(formWithFields);

        const initialValues: Record<string, string> = {};
        (fields || []).forEach((field: FormField) => {
          initialValues[field.id] = '';
        });
        setValues(initialValues);
      }
    } catch (error) {
      logger.error('Error loading form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!form) return false;

    const errors: Record<string, string> = {};
    form.fields.forEach((field) => {
      const value = values[field.id] || '';

      if (field.required && !value.trim()) {
        errors[field.id] = 'Pflichtfeld';
      }

      if (field.type === 'EMAIL' && value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.id] = 'Ungültige E-Mail';
        }
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !validateForm()) return;

    setIsSubmitting(true);

    try {
      const result = await submitPublicForm(form, values);
      if (result.success) {
        setIsSubmitted(true);
      }
    } catch (error) {
      logger.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (fieldId: string, value: string) => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      trackStart();
    }
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const renderField = (field: FormField) => {
    const value = values[field.id] || '';
    const fieldError = fieldErrors[field.id];
    const isFocused = focusedField === field.id;
    const hasValue = value.length > 0;

    const baseWrapperClass = `relative transition-all duration-300 ${
      isFocused ? 'scale-[1.01]' : ''
    }`;

    const baseInputClass = `w-full px-5 py-4 border-2 rounded-2xl bg-white transition-all duration-300 outline-none text-gray-900 ${
      fieldError
        ? 'border-red-300 bg-red-50/50'
        : isFocused
        ? 'border-gray-300 shadow-lg'
        : 'border-gray-100 hover:border-gray-200'
    }`;

    const labelClass = `absolute left-5 transition-all duration-300 pointer-events-none ${
      isFocused || hasValue
        ? '-top-2.5 text-xs font-medium px-2 bg-white'
        : 'top-4 text-gray-400'
    }`;

    switch (field.type) {
      case 'TEXTAREA':
        return (
          <div className={baseWrapperClass}>
            <textarea
              value={value}
              onChange={(e) => handleChange(field.id, e.target.value)}
              onFocus={() => setFocusedField(field.id)}
              onBlur={() => setFocusedField(null)}
              rows={4}
              className={`${baseInputClass} resize-none`}
            />
            <span className={labelClass} style={{ color: isFocused || hasValue ? primaryColor : undefined }}>
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </span>
          </div>
        );

      case 'SELECT':
        return (
          <div className={baseWrapperClass}>
            <select
              value={value}
              onChange={(e) => handleChange(field.id, e.target.value)}
              onFocus={() => setFocusedField(field.id)}
              onBlur={() => setFocusedField(null)}
              className={`${baseInputClass} appearance-none cursor-pointer`}
            >
              <option value="">{field.placeholder || 'Bitte auswählen...'}</option>
              {(Array.isArray(field.options) ? field.options : []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className={labelClass} style={{ color: isFocused || hasValue ? primaryColor : undefined }}>
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </span>
            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        );

      case 'MULTISELECT':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </p>
            {(Array.isArray(field.options) ? field.options : []).map((option) => {
              const selectedOptions = value ? value.split(',') : [];
              const isSelected = selectedOptions.includes(option);
              return (
                <label
                  key={option}
                  className={`flex items-center gap-4 p-4 border-2 rounded-2xl cursor-pointer transition-all duration-300 ${
                    isSelected
                      ? 'border-gray-300 shadow-md'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                  style={isSelected ? { borderColor: `${primaryColor}40`, backgroundColor: `${primaryColor}05` } : {}}
                >
                  <div
                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${
                      isSelected ? 'border-transparent' : 'border-gray-300'
                    }`}
                    style={isSelected ? { backgroundColor: primaryColor } : {}}
                  >
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const newSelected = e.target.checked
                        ? [...selectedOptions, option]
                        : selectedOptions.filter((o) => o !== option);
                      handleChange(field.id, newSelected.join(','));
                    }}
                    className="sr-only"
                  />
                  <span className="text-gray-700 font-medium">{option}</span>
                </label>
              );
            })}
          </div>
        );

      case 'EMAIL':
        return (
          <div className={baseWrapperClass}>
            <input
              type="email"
              value={value}
              onChange={(e) => handleChange(field.id, e.target.value)}
              onFocus={() => setFocusedField(field.id)}
              onBlur={() => setFocusedField(null)}
              className={baseInputClass}
            />
            <span className={labelClass} style={{ color: isFocused || hasValue ? primaryColor : undefined }}>
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </span>
          </div>
        );

      case 'PHONE':
        return (
          <div className={baseWrapperClass}>
            <input
              type="tel"
              value={value}
              onChange={(e) => handleChange(field.id, e.target.value)}
              onFocus={() => setFocusedField(field.id)}
              onBlur={() => setFocusedField(null)}
              className={baseInputClass}
            />
            <span className={labelClass} style={{ color: isFocused || hasValue ? primaryColor : undefined }}>
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </span>
          </div>
        );

      case 'RATING': {
        const ratingOptions = (field.options && typeof field.options === 'object' && !Array.isArray(field.options))
          ? field.options as RatingFieldOptions
          : {};
        const currentRating = parseInt(value) || 0;

        return (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleChange(field.id, star.toString())}
                  className="p-1 focus:outline-none rounded transition-transform hover:scale-110"
                  style={{ color: star <= currentRating ? primaryColor : '#E5E7EB' }}
                >
                  <Star
                    className="w-8 h-8 transition-colors"
                    style={{
                      fill: star <= currentRating ? primaryColor : '#E5E7EB',
                      color: star <= currentRating ? primaryColor : '#E5E7EB'
                    }}
                  />
                </button>
              ))}
            </div>
            {(ratingOptions.label_low || ratingOptions.label_high) && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>{ratingOptions.label_low || ''}</span>
                <span>{ratingOptions.label_high || ''}</span>
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <div className={baseWrapperClass}>
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(field.id, e.target.value)}
              onFocus={() => setFocusedField(field.id)}
              onBlur={() => setFocusedField(null)}
              className={baseInputClass}
            />
            <span className={labelClass} style={{ color: isFocused || hasValue ? primaryColor : undefined }}>
              {field.label}{field.required && <span className="text-red-400 ml-1">*</span>}
            </span>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <section ref={ref} className="py-20 md:py-28 px-4">
        <div className="max-w-xl mx-auto flex justify-center">
          <div
            className="w-10 h-10 border-3 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}
          />
        </div>
      </section>
    );
  }

  if (!form) {
    return (
      <section ref={ref} className="py-20 md:py-28 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{settings.title}</h2>
          <p className="text-gray-500">Kein Formular ausgewählt</p>
        </div>
      </section>
    );
  }

  if (isSubmitted) {
    return (
      <section ref={ref} className="py-20 md:py-28 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="relative inline-block mb-8">
            <div
              className="absolute inset-0 rounded-full blur-2xl opacity-30"
              style={{ backgroundColor: primaryColor }}
            />
            <div
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Check className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Vielen Dank!</h2>
          <p className="text-lg text-gray-600">Deine Angaben wurden erfolgreich übermittelt. Ich melde mich bald bei dir.</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="py-20 md:py-28 px-4 relative overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}05 0%, transparent 50%, ${primaryColor}08 100%)`,
        }}
      />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl -translate-y-1/2 translate-x-1/2" style={{ backgroundColor: primaryColor }} />

      <div className="relative max-w-xl mx-auto">
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          >
            <Sparkles className="w-4 h-4" />
            <span>Jetzt starten</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100 space-y-6">
          {form.fields.map((field) => (
            <div key={field.id}>
              {renderField(field)}
              {fieldErrors[field.id] && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  {fieldErrors[field.id]}
                </p>
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={isSubmitting}
            className="group w-full py-4 text-white rounded-2xl font-semibold text-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-3"
            style={{
              backgroundColor: primaryColor,
              boxShadow: `0 10px 40px -10px ${primaryColor}60`,
            }}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                Absenden
                <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="text-center text-sm text-gray-400 mt-4">
            Deine Daten werden vertraulich behandelt
          </p>
        </form>
      </div>
    </section>
  );
});

FormBlock.displayName = 'FormBlock';
